// ── mediaSession — что играет: уведомление на телефоне и экран блокировки ────
// В приложении два источника звука: большой плеер и фоновое аудио нижней панели.
// Раньше уведомлением всегда управлял плеер — когда играло фоновое аудио, там была
// чужая карточка и 00:00. Теперь у каждого источника свой «пульт», а система видит
// того, кто последним начал играть (claim):
//   const ms = mediaSession('player');
//   ms.claim();  ms.setMetadata({...});  ms.setPlaybackState('playing');
//   ms.setPositionState({ duration, position, playbackRate });
//   ms.setActionHandler('play' | 'pause' | 'previoustrack' | 'nexttrack' | 'seekto', fn);
//
// Android (Capacitor): свой плагин AudioCardsMedia (android/…/AudioCardsMediaService).
// Позицию отправляем только когда она «прыгнула» (перемотка, пауза, скорость) —
// дальше полоску в уведомлении ведёт сама система; раньше плагин позицию не знал.
// Браузер: navigator.mediaSession.

import { registerPlugin } from '@capacitor/core';

const isNative = !!(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.());

// Прокси плагина нельзя возвращать из async-функции и передавать в await: Promise
// проверяет у него then, вызов зависает (из-за этого уведомление не появлялось).
const plugin = isNative ? registerPlugin('AudioCardsMedia') : null;

const ACTIONS = ['play', 'pause', 'previoustrack', 'nexttrack', 'seekto'];
const owners = {};
let current = null;

// ── Отправка в систему ──────────────────────────────────────────────────────
let listening = false;
function listenNative() {
  if (listening || !plugin?.addListener) return;
  listening = true;
  const map = { prev: 'previoustrack', next: 'nexttrack', play: 'play', pause: 'pause', seekto: 'seekto' };
  Promise.resolve(plugin.addListener('mediaAction', ({ action, position }) => {
    const h = current && owners[current].handlers[map[action]];
    if (!h) return;
    try { h(action === 'seekto' ? { seekTime: (position || 0) / 1000 } : {}); } catch (e) { console.warn('[MediaSession]', e); }
  })).catch((e) => console.warn('[MediaSession] addListener failed:', e?.message));
}

function pushMetadata(md) {
  if (!md) return;
  if (isNative) {
    listenNative();
    plugin.setMetadata({ title: md.title || '', artist: md.artist || '', artworkBase64: md.capArtworkDataUri || '' })
      .catch((e) => console.warn('[MediaSession] setMetadata:', e?.message));
    return;
  }
  try {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new window.MediaMetadata({ title: md.title, artist: md.artist, album: md.album, artwork: md.artwork || [] });
    }
  } catch { /* старый браузер */ }
}

function pushState(state) {
  if (!state) return;
  if (isNative) {
    listenNative();
    plugin.setPlaybackState({ state: state === 'playing' ? 'playing' : 'paused' })
      .catch((e) => console.warn('[MediaSession] setPlaybackState:', e?.message));
    return;
  }
  try { if ('mediaSession' in navigator) navigator.mediaSession.playbackState = state; } catch { /* нет API */ }
}

// Сколько должно быть сейчас, если с прошлой отправки звук шёл без скачков.
let sent = null; // { duration, position, rate, playing, at }
function pushPosition(pos, playing, force) {
  if (!pos || !(pos.duration > 0)) return;
  const position = Math.max(0, Math.min(pos.position ?? 0, pos.duration));
  const rate = pos.playbackRate || 1;
  if (isNative) {
    const now = Date.now();
    const expected = sent ? sent.position + (sent.playing ? ((now - sent.at) / 1000) * sent.rate : 0) : -1;
    const jumped = !sent || force || sent.duration !== pos.duration || sent.rate !== rate || sent.playing !== playing
      || Math.abs(expected - position) > 1.5;
    if (!jumped) return;
    sent = { duration: pos.duration, position, rate, playing, at: now };
    // Старые версии приложения (до 1.4.0) этого метода не знают — тихо пропускаем.
    Promise.resolve(plugin.setPositionState({ duration: pos.duration, position, playbackRate: rate, playing })).catch(() => {});
    return;
  }
  try {
    navigator.mediaSession?.setPositionState?.({ duration: pos.duration, playbackRate: rate, position });
  } catch { /* позиция вне длины и т.п. */ }
}

function pushHandlers(handlers) {
  if (isNative) { listenNative(); return; } // на телефоне обработчики читаются из owners[current]
  if (!('mediaSession' in navigator)) return;
  for (const a of ACTIONS) {
    try { navigator.mediaSession.setActionHandler(a, handlers[a] || null); } catch { /* действие не поддерживается */ }
  }
}

function applyOwner() {
  const o = current && owners[current];
  if (!o) return;
  sent = null;
  pushHandlers(o.handlers);
  pushMetadata(o.metadata);
  pushState(o.state);
  pushPosition(o.position, o.state === 'playing', true);
}

// Значок для уведомления на телефоне (картинка как data:, плагин не скачивает по адресу).
let iconPromise = null;
export function appIconDataUri() {
  if (!iconPromise) {
    iconPromise = fetch('/logo192.png').then((r) => r.blob()).then((blob) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    })).catch(() => null);
  }
  return iconPromise;
}

// ── Пульт источника ─────────────────────────────────────────────────────────
export function mediaSession(id) {
  if (!owners[id]) owners[id] = { handlers: {}, metadata: null, state: null, position: null };
  const me = owners[id];
  const mine = () => current === id;
  return {
    // Этот источник начал играть — уведомление теперь про него.
    claim() { if (!mine()) { current = id; applyOwner(); } },
    // Источник остановлен совсем — отдать уведомление тому, кто ещё играет.
    release() {
      if (!mine()) return;
      current = Object.keys(owners).find((k) => k !== id && owners[k].state === 'playing') || null;
      applyOwner();
    },
    setMetadata(md) { me.metadata = md; if (mine()) pushMetadata(md); },
    setPlaybackState(state) {
      me.state = state;
      if (mine()) { pushState(state); pushPosition(me.position, state === 'playing', true); }
    },
    setPositionState(pos) { me.position = pos; if (mine()) pushPosition(pos, me.state === 'playing', false); },
    setActionHandler(action, fn) {
      if (fn) me.handlers[action] = fn; else delete me.handlers[action];
      if (mine()) pushHandlers(me.handlers);
    },
    clearActionHandlers() { me.handlers = {}; if (mine()) pushHandlers(me.handlers); },
  };
}
